import _ from 'lodash'
import { withDependencies, multi } from '@wix/thunderbolt-ioc'
import { IViewerManagerImplementor } from 'thunderbolt-viewer-manager-types'
import {
	DataRequirementsCheckersDebugMap,
	IDataRequirementsChecker,
	IDataRequirementsCheckersDebugMap,
	LoggerSymbol,
	ILogger,
	LOADING_PHASES,
	DSViewModeAPISymbol,
	LayoutDoneServiceSymbol,
	LayoutDiagnosticSymbol,
	ILayoutDoneService,
	LayoutDiagnosticService,
	BrowserWindowSymbol,
	BrowserWindow,
	DataRequirementsCheckerSymbol,
	IViewMode,
} from '@wix/thunderbolt-symbols'
import { INavigationManager, NavigationManagerSymbol } from 'feature-navigation-manager'
import { INavigationPhases, NavigationPhasesSymbol } from 'feature-navigation-phases'
import { LayoutRegistrarSymbol } from 'ds-feature-viewer-manager-layout-api'
import type { LayoutRegistrar } from 'ds-feature-viewer-manager-layout-api'
import { IsDuringNavigationInteractionSymbol } from 'ds-feature-editor-navigation'
import type { IIsDuringNavigationInteraction } from 'ds-feature-editor-navigation'

type Phases = {
	waitForViewModeChange: boolean
	waitForNavigationEnd: boolean
	waitForDataRequirements: boolean
	waitForLayoutDone: boolean
}

type WaitStatus = {
	timedOut: boolean
	start: number
	durations: Record<keyof Phases, number>
}

/** Thrown if the wait was longer than the SOQ timeout period. */
class WaitForViewerTimeoutError extends Error {
	constructor(message: string) {
		super(message)
		this.name = 'WaitForViewerTimeoutError'
	}
}

/** Thrown if the wait is over but took too long (more than the SOQ timeout). */
class WaitForViewerTooLongError extends Error {
	constructor(message: string) {
		super(message)
		this.name = 'WaitForViewerTooLongError'
	}
}

export const ViewerManagerUpdateStatusApi = withDependencies(
	[
		multi(DataRequirementsCheckerSymbol),
		LoggerSymbol,
		DSViewModeAPISymbol,
		LayoutRegistrarSymbol,
		LayoutDoneServiceSymbol,
		LayoutDiagnosticSymbol,
		NavigationManagerSymbol,
		NavigationPhasesSymbol,
		BrowserWindowSymbol,
		IsDuringNavigationInteractionSymbol,
	],
	(
		checksImplementors: Array<IDataRequirementsChecker>,
		logger: ILogger,
		{ isDuringViewModeSwitch, registerToNextSwitchViewModeDone }: IViewMode,
		{ registerToNextRenderDone }: LayoutRegistrar,
		{ isRenderPending }: ILayoutDoneService,
		{ getDiagnosticState }: LayoutDiagnosticService,
		{ waitForNavigationEnd, shouldBlockRender }: INavigationManager,
		navigationPhases: INavigationPhases,
		window: NonNullable<BrowserWindow>,
		{ isDuringNavigationInteraction }: IIsDuringNavigationInteraction
	): IViewerManagerImplementor<'updateStatus'> & IDataRequirementsCheckersDebugMap => {
		let firstWaitForViewerInvocation = true

		const dataRequirementsCheckersDebugMap = checksImplementors.reduce<DataRequirementsCheckersDebugMap>(
			(acc, { featureName }) => ({ ...acc, [featureName]: true }),
			{}
		)

		const waitForDataRequirements = async () => {
			logger.phaseStarted(LOADING_PHASES.DS_WAIT_FOR_DATA_REQUIREMENTS)
			const dataRequirementsCheckers = checksImplementors.map(async ({ featureName, pendingRequirements }) => {
				// @ts-ignore
				logger.phaseStarted(LOADING_PHASES[`DS_PENDING_REQUIREMENTS_${featureName}`])
				const promise = pendingRequirements()
				dataRequirementsCheckersDebugMap[featureName] = false

				await promise
				dataRequirementsCheckersDebugMap[featureName] = true
				// @ts-ignore
				logger.phaseEnded(LOADING_PHASES[`DS_PENDING_REQUIREMENTS_${featureName}`])
			})

			await Promise.all(dataRequirementsCheckers)
			logger.phaseEnded(LOADING_PHASES.DS_WAIT_FOR_DATA_REQUIREMENTS)
		}

		const setWaitTimeout = (fn: () => void, ttl: number): (() => void) => {
			const timeout = setTimeout(fn, ttl)
			return () => clearTimeout(timeout)
		}

		const setVisibilityListener = (fn: (isVisible: boolean) => void): (() => void) => {
			const listener = () => {
				fn(window.document.visibilityState === 'visible')
			}
			window.document.addEventListener('visibilitychange', listener)
			return () => {
				window.document.removeEventListener('visibilitychange', listener)
			}
		}

		const waitForViewer = async () => {
			// setup
			const firstInvocation = firstWaitForViewerInvocation
			firstWaitForViewerInvocation = false

			const SOQ_TIMEOUT_TTL = 35 * 1000
			const phases: Phases = {
				waitForViewModeChange: false,
				waitForNavigationEnd: false,
				waitForDataRequirements: false,
				waitForLayoutDone: false,
			}
			const waitStatus: WaitStatus = {
				timedOut: false,
				start: Date.now(),
				durations: {
					waitForViewModeChange: 0,
					waitForNavigationEnd: 0,
					waitForDataRequirements: 0,
					waitForLayoutDone: 0,
				},
			}

			const measureStep = async (name: keyof Phases, fn: () => Promise<void>) => {
				const start = Date.now()
				phases[name] = true
				await fn()
				phases[name] = false
				waitStatus.durations[name] = Date.now() - start
			}

			const collectFlags = () => ({
				isDuringViewModeSwitch: isDuringViewModeSwitch(),
				isDuringNavigationInteraction: isDuringNavigationInteraction(),
				isLayoutPending: isRenderPending(),
				shouldBlockRender: shouldBlockRender(),
			})

			// watchdogs
			const clearWaitTimeout = setWaitTimeout(() => {
				waitStatus.timedOut = true
				const flags = collectFlags()
				const getFalsyDataRequirementCheckers = () => _.keys(_.omitBy(dataRequirementsCheckersDebugMap))
				const extra = {
					dataRequirementCheckers: getFalsyDataRequirementCheckers(),
					...getDiagnosticState(),
				}
				const pendingPhases = Object.keys(_.pickBy(phases, (value, _key) => value))
				logger.captureError(new WaitForViewerTimeoutError(`Pending phases: ${pendingPhases.join(', ')}`), {
					tags: {
						feature: 'updateStatus',
						methodName: 'waitForViewer',
						unfinishedNavigationPhases: navigationPhases.getUnfinishedPhases().sort().join(',') || 'NONE',
						firstInvocation,
						...phases,
						...flags,
					},
					extra,
				})
				clearVisibilityListener()
			}, SOQ_TIMEOUT_TTL)

			const clearVisibilityListener = setVisibilityListener((isVisible) => {
				if (!isVisible) {
					clearWaitTimeout()
					clearVisibilityListener()
				}
			})

			// wait logic
			try {
				if (isDuringViewModeSwitch()) {
					await measureStep(
						'waitForViewModeChange',
						async () => await new Promise<void>(registerToNextSwitchViewModeDone)
					)
				}

				if (isDuringNavigationInteraction()) {
					await measureStep('waitForNavigationEnd', waitForNavigationEnd)
				}

				await measureStep('waitForDataRequirements', waitForDataRequirements)

				if (isRenderPending()) {
					await measureStep('waitForLayoutDone', async () => new Promise<void>(registerToNextRenderDone))
				}

				// cleanup
				clearWaitTimeout()
				clearVisibilityListener()
				if (waitStatus.timedOut) {
					const slowPhases = Object.keys(
						_.pickBy(waitStatus.durations, (value, _key) => value > SOQ_TIMEOUT_TTL)
					)
					logger.captureError(new WaitForViewerTooLongError(`Slow phases: ${slowPhases.join(', ')}`), {
						tags: {
							...collectFlags(),
							feature: 'updateStatus',
							methodName: 'waitForViewer',
							firstInvocation,
							isViewModeSwitchSlow: waitStatus.durations.waitForViewModeChange > SOQ_TIMEOUT_TTL,
							isNavigationInteractionSlow: waitStatus.durations.waitForNavigationEnd > SOQ_TIMEOUT_TTL,
							isDataRequirementsSlow: waitStatus.durations.waitForDataRequirements > SOQ_TIMEOUT_TTL,
							isLayoutSlow: waitStatus.durations.waitForLayoutDone > SOQ_TIMEOUT_TTL,
						},
						extra: {
							duration: Date.now() - waitStatus.start,
							navigationPhasesDuration: navigationPhases.getPhases(),
							...waitStatus.durations,
						},
					})
				}
			} catch (err) {
				// don't report exceptions during waitForViewer as timeout errors since those are not real timeouts. DM can handle those and
				// try to flush their SOQ next time
				clearWaitTimeout()
				clearVisibilityListener()
				logger.captureError(err, {
					tags: { feature: 'updateStatus', methodName: 'waitForViewer', firstInvocation },
				})
				throw err
			}
		}

		return {
			updateStatus: {
				waitForDataRequirements,
				waitForViewer,
			},
			getDataRequirementsCheckersDebugMap: () => dataRequirementsCheckersDebugMap,
		}
	}
)
